import { EventEmitter, Injectable, Output } from '@angular/core';

import { MatSnackBar } from '@angular/material/snack-bar';
import { C3Service, geo, mrk, Vec3, Color3, View, ug } from '@usimweb/ceetron';
import { Point, NozzlePoint } from '@usimweb/core/models';

import { Subscription } from 'rxjs';
import { Helper } from './helper';

@Injectable({
  providedIn: 'root',
})
export class PointSelectionService {
  private infoVertices: Point[] = [];
  private infoPoints: NozzlePoint[] = [];

  private originPoints: Vec3[] = [];
  private originNormals: Vec3[] = [];

  private showVectors: boolean = false;
  private isNormal: boolean = true;
  private offset: number = 0.1;

  private subscripton: Subscription = null as any;

  @Output() pointAmountEmitter: EventEmitter<number> = new EventEmitter<number>();

  constructor(private c3Service: C3Service, private snackBar: MatSnackBar) {}

  /**
   * Listen to click event on the viewer
   * Checks if current model was selected
   */
  public initSelectionListener(modelId: number) {
    this.unsubscribe();
    this.clearPoints(modelId);

    this.c3Service._view[modelId.toString()].overlay.infoBoxVisible = false;
    this.subscripton = this.c3Service.elementPicked[modelId.toString()].subscribe((hitItem: ug.HitItem) => {
      if (hitItem.anythingHit) {
        const origin: Vec3 = hitItem.intersectionPoint;
        const normal: Vec3 = hitItem.intersectionPointNormal;

        this.draw(origin, normal, modelId);

        const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);
        view.requestRedraw();
      }
    });
  }

  public unsubscribe() {
    if (this.subscripton !== null) {
      this.subscripton?.unsubscribe();
    }
  }

  /**
   * Removes the selected points from the viewer
   */
  public clearPoints(modelId: number) {
    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);

    for (let i = view.getModelArray().length; i >= 1; i--) {
      if (view.getModelAt(i)) {
        view.removeModelAt(i);
      }
    }

    this.infoVertices = [];
    this.infoPoints = [];
    this.originPoints = [];
    this.originNormals = [];
    this.pointAmountEmitter.emit(this.infoPoints.length);
  }

  /**
   * Add points into the current view
   * Drawes a line into the current view
   * Handles the vertices order
   * @param point Array with x, y, z values
   */
  private draw(currentPoint: Vec3, normal: Vec3, modelId: number, negate = true, scale = true) {
    let geoModel = new geo.GeometryModel();
    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);

    let timestep = 0;

    const markupModel = new mrk.MarkupModel();

    normal = negate === true ? normal.getNegated() : normal;

    this.originPoints.push(currentPoint);
    this.originNormals.push(normal);

    normal = scale === true ? Vec3.scale(normal, 0.1) : normal;
    const offSetPoint = scale === true ? this.movePoint(currentPoint, normal) : currentPoint;

    // const offSetPoint = this.movePoint(currentPoint, normal)
    this.addPoint(offSetPoint, markupModel, 'Point');

    if (this.infoVertices.length > 0) {
      timestep = this.addLine(offSetPoint, geoModel, view);
    }

    if (this.showVectors) {
      this.addArrow(offSetPoint, normal, view);
    }

    view.addModel(markupModel);

    this.infoVertices.push({
      x: offSetPoint.x,
      y: offSetPoint.y,
      z: offSetPoint.z,
    });
    this.infoPoints.push({
      position: offSetPoint,
      orientation: normal,
      timestep: timestep,
    });

    this.pointAmountEmitter.emit(this.infoPoints.length);
  }

  private addPoint(currentPoint: Vec3, markupModel: mrk.MarkupModel, text: string) {
    markupModel.name = 'point';

    let labelsPart = markupModel.addLabelsPart();
    const index: number = this.infoPoints.length + 1;
    let labelText = text + ': ' + index;
    labelsPart.customData = { partType: 'point', index: index };

    let point = new Vec3(currentPoint.x, currentPoint.y, currentPoint.z);
    labelsPart.add(labelText, point);
  }

  private addArrow(currentPoint: Vec3, normal: Vec3, view: View) {
    const markupModel = new mrk.MarkupModel();
    let arrow = markupModel.addArrowsPart();

    // normal = Vec3.scale(normal, 0.1)
    arrow.addArrow(currentPoint, normal);

    arrow.color = new Color3(0, 1, 0);
    arrow.customData = { partType: 'arrow' };

    view.addModel(markupModel);
  }

  private addLine(currentPoint: Vec3, geoModel: geo.GeometryModel, view: View) {
    const indices: number[] = [0, 1];
    let vertices: ArrayLike<number>;

    const lastPoint = this.handlePointsOrder();
    vertices = [lastPoint.x, lastPoint.y, lastPoint.z, currentPoint.x, currentPoint.y, currentPoint.z];

    let part = geoModel.addPart();

    part.customData = { partType: 'line' };

    let mesh = new geo.MeshIndexedLines(vertices, indices);
    part.mesh = mesh;
    part.settings.color = new Color3(1, 0, 0);

    view.name = 'line';
    view.addModel(geoModel);

    const lastPointVec = new Vec3(lastPoint.x, lastPoint.y, lastPoint.z);

    return lastPointVec.getPointDistance(currentPoint);
  }

  private movePoint(point: Vec3, direction: Vec3) {
    const scaled = Vec3.scale(direction, this.offset * -1);
    return Vec3.add(point, scaled);
  }

  public toggleVectors(modelId: number, isChecked: boolean) {
    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);

    if (isChecked) {
      this.showVectors = true;

      this.infoPoints.forEach((item) => {
        this.addArrow(
          (item.position as any) || new Vec3(0, 0, 0),
          (item.orientation as any) || new Vec3(0, 0, 0),
          view
        );
      });

      view.requestRedraw();

      return;
    }

    this.showVectors = false;
    this.clearVectors(view);
  }

  public setOrientation(isNormal: boolean) {
    isNormal === true ? (this.isNormal = true) : (this.isNormal = false);
  }

  public getOrientation(): boolean {
    return this.isNormal;
  }

  public clearVectors(view: View) {
    let modelArray = view.getModelArray();
    let foundArrow = false;

    modelArray.forEach((model: any, i: any) => {
      if (model.hasOwnProperty('m_partArr')) {
        const customData = model['m_partArr'][0]['m_customData'];
        if (customData['partType'] === 'arrow') {
          view.removeModelAt(i);
          foundArrow = true;
        }
      }
    });

    if (foundArrow) {
      setTimeout(() => {
        this.clearVectors(view);
      });
    }
  }

  /**
   * Get last points for correct line order
   * Return a dict of Vec3Like
   */
  private handlePointsOrder() {
    const tmpArr = Helper.Array.deepCopy(this.infoVertices);
    const lastPoint = tmpArr.pop();
    return { x: lastPoint.x, y: lastPoint.y, z: lastPoint.z };
  }

  /**
   * Reorder current points
   * @param modelId id of the current model
   */
  private orderPoints(modelId: number) {
    const tmpPoints = Helper.Array.deepCopy(this.originPoints) as Vec3[];
    const tmpNormals = Helper.Array.deepCopy(this.originNormals) as Vec3[];

    this.clearPoints(modelId);

    tmpPoints.forEach((point, index: number) => {
      this.draw(point || new Vec3(0, 0, 0), tmpNormals[index] || new Vec3(0, 0, 0), modelId, false);
    });

    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);
    view.requestRedraw();
  }

  /**
   * Get vertices from specific model
   */
  public getInfoVertices() {
    return this.infoVertices;
  }

  /**
   * Get points from specific model
   */
  public getInfoPoints() {
    return this.infoPoints;
  }

  public setPointOffset(value: number, modelId: number) {
    this.offset = value;

    const tmpPoints = Helper.Array.deepCopy(this.originPoints) as Vec3[];
    const tmpNormals = Helper.Array.deepCopy(this.originNormals) as Vec3[];

    this.clearPoints(modelId);

    tmpPoints.forEach((point, index: number) => {
      this.draw(point || new Vec3(0, 0, 0), tmpNormals[index] || new Vec3(0, 0, 0), modelId, false);
    });

    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);
    view.requestRedraw();

    this.snackBar.open('Set new nozzle offset...', 'OK', { duration: 2000 });
  }

  public getPointOffset() {
    return this.offset;
  }

  public setGravity(modelId: number) {
    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);
    const markupModel = new mrk.MarkupModel();

    // this.clearVectors(view);

    const gravityVec3 = new Vec3(0, 0, 9.81);
    const scaled = Vec3.scale(gravityVec3, 0.1);
    const gravites: Vec3[] = [];

    this.originNormals.forEach((normal) => {
      const gravityVector = Vec3.sub(normal, scaled);
      gravites.push(gravityVector);
    });

    this.originPoints.forEach((point) => {
      this.addPoint(point, markupModel, 'gravity 1');
    });

    for (let i = 0; i < this.originPoints.length; i++) {
      this.addArrow(this.originPoints[i], scaled, view);
    }

    view.addModel(markupModel);
    view.requestRedraw();
  }

  /**
   * Get points from specific model
   * @param data contains a @class Point object
   */
  public updateInfoPoints(point: Vec3, normal: Vec3, modelId: number) {
    this.draw(point, normal, modelId, false, false);

    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);
    view.requestRedraw();
  }

  /**
   * Delete a point & reorder all points
   * Draw new vertices
   * @param index of the delete point
   * @param modelId id of the current model
   */
  public removeInfoPointAt(index: number, modelId: number) {
    this.infoPoints.splice(index - 1, 1);
    this.originPoints.splice(index - 1, 1);
    this.originNormals.splice(index - 1, 1);

    const view: View = this.c3Service._viewer[modelId.toString()].getViewAt(0);

    let modelArray = view.getModelArray();

    let realIndex = 0;
    modelArray.forEach((model: any, i: any) => {
      if (model.hasOwnProperty('m_partArr')) {
        const customData = model['m_partArr'][0]['m_customData'];
        if (customData['partType'] === 'point') {
          if (customData['index'] === index) {
            realIndex = i;
          }
        }
      }
    });

    view.removeModelAt(realIndex);

    this.pointAmountEmitter.emit(this.infoPoints.length);
    this.orderPoints(modelId);
  }
}
